package link.erpango.quip.domain.noteimport

import link.erpango.quip.domain.entities.DExtractionError

/** Where the assistant was opened from, i.e. what the user is creating. */
enum class NoteImportTarget(val code: String) {
    VEHICLE("vehicle"),
    MAINTENANCE("maintenance"),
    SPARE_PART("spare_part"),
    BULK("bulk"),
}

/**
 * Analytics events of the assistant. The parameter is always an enum code or a count bucket:
 * never note text, plates, VINs or brands. Only these types can be logged, so content can
 * not slip into analytics by accident.
 */
sealed class NoteImportEvent(val name: String, val param: String) {
    class Opened(target: NoteImportTarget) : NoteImportEvent("note_import_opened", target.code)

    class LocalAnalyzed(draft: ImportDraft) : NoteImportEvent("note_import_local_analyzed", countBucket(draft))

    class AiConsent(granted: Boolean) : NoteImportEvent("note_import_ai_consent", if (granted) "granted" else "declined")

    data object AiRequested : NoteImportEvent("note_import_ai_requested", "")

    class AiResult(error: DExtractionError?) : NoteImportEvent("note_import_ai_result", resultCode(error))

    class Applied(target: NoteImportTarget) : NoteImportEvent("note_import_applied", target.code)

    class BulkCreated(outcome: String) : NoteImportEvent("note_import_bulk_created", outcome)
}

/** "v1_m2_p3+" style buckets: counts capped at 3 so the value never identifies a note. */
internal fun countBucket(draft: ImportDraft): String {
    fun bucket(count: Int) = if (count >= 3) "3+" else count.toString()
    return "v${bucket(draft.vehicles.size)}_m${bucket(draft.maintenances.size)}_p${bucket(draft.spareParts.size)}"
}

internal fun resultCode(error: DExtractionError?): String = when (error) {
    null -> "ok"
    is DExtractionError.RateLimited -> "rate_limited"
    is DExtractionError.DailyQuotaExceeded -> "daily_quota"
    is DExtractionError.GlobalQuotaExceeded -> "global_quota"
    DExtractionError.Unavailable -> "unavailable"
    DExtractionError.Timeout -> "timeout"
    DExtractionError.ProviderError -> "error"
    DExtractionError.ExtractionFailed -> "failed"
    DExtractionError.Network -> "network"
    DExtractionError.NoteTooLong -> "too_long"
    DExtractionError.EmptyNote -> "empty"
    DExtractionError.Unauthorized -> "unauthorized"
    DExtractionError.Unknown -> "unknown"
}
